import React, { createContext, useContext, useState, useEffect } from 'react';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { ensureRolePermissionsDoc, getRolePermissions, getUserRole, getUserProfile } from '../firebase/services';
import { DEFAULT_ROLE_PERMISSIONS, mergePermissionsMaps, normalizePermissionsMap, PERMISSIONS } from '../Utils/permissions';

const UserContext = createContext();

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [role, setRole] = useState(null);
  const [userData, setUserData] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const auth = getAuth();

  const isLocalEmulator = typeof window !== 'undefined'
    && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      // Keep guards in the loading state until role/permissions are resolved,
      // otherwise a freshly signed-in user is briefly seen with no permissions
      // and gets redirected back to the login page.
      setLoading(true);
      if (!currentUser) {
        setUser(null);
        setRole(null);
        setUserData(null);
        setPermissions({});
        setLoading(false);
        return;
      }

      try {
        const userRole = await getUserRole(currentUser.uid);
        const profile = await getUserProfile(currentUser.uid);

        const roleFromProfile = typeof profile?.role === 'string' ? profile.role.toLowerCase() : null;
        let effectiveRole = userRole || roleFromProfile;

        // Emulator bootstrap: if the Firestore user doc doesn't exist yet, allow a known admin email
        // to access the admin panel during local development.
        if (!effectiveRole && isLocalEmulator) {
          const email = (currentUser.email || '').toLowerCase();
          if (email === 'admin@vetcare.com') {
            effectiveRole = 'admin';
          }
        }

        // Ensure role permission docs exist (admin bootstrap)
        if (effectiveRole === 'admin') {
          try {
            const roles = ['admin', 'moderator', 'shipping', 'shipping_company'];
            for (const r of roles) {
              // eslint-disable-next-line no-await-in-loop
              await ensureRolePermissionsDoc(r, DEFAULT_ROLE_PERMISSIONS[r] || {});
            }
          } catch (e) {
            console.warn('Unable to ensure role permission docs:', e);
          }
        }

        const roleDefaults = DEFAULT_ROLE_PERMISSIONS[effectiveRole] || {};
        const roleRemotePermissions = await getRolePermissions(effectiveRole);
        const userOverride = normalizePermissionsMap(profile?.permissionsOverride);
        const effectivePermissions = mergePermissionsMaps(roleDefaults, roleRemotePermissions, userOverride);

        // Publish user together with its permissions so guards never see a
        // signed-in user with an empty permissions map.
        setRole(effectiveRole);
        setUserData(profile);
        setPermissions(effectivePermissions);
        setUser(currentUser);
      } catch (e) {
        console.error('Unable to load user role/permissions:', e);
        setRole(null);
        setUserData(null);
        setPermissions({});
        setUser(currentUser);
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, [auth, isLocalEmulator]);

  return (
    <UserContext.Provider value={{ user, role, userData, permissions, loading, isAdminPanelUser: !!permissions?.[PERMISSIONS.ADMIN_PANEL_ACCESS] }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => useContext(UserContext);
